import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AnalyticsHub from '@/components/analytics/AnalyticsHub';
import PerformanceCard from './PerformanceCard';

interface SuperAdminHomeProps {
  clients: any[];
  rooms: any[];
  sites: any[];
  alerts: any[];
  totalCapacity: number;
  checkedInCount: number;
  dailyRosterCount: number;
  userName: string;
  onSelectFacility?: (siteId: string) => void;
  showPerformanceCard?: boolean;
}

/**
 * Shared "Super Admin Home" view.
 * Used by AdminDashboard (Super Admin) and ManagerDashboard (Program Director).
 */
const SuperAdminHome: React.FC<SuperAdminHomeProps> = ({
  clients,
  rooms,
  sites,
  alerts,
  totalCapacity,
  checkedInCount,
  dailyRosterCount,
  userName,
  onSelectFacility,
  showPerformanceCard = true,
}) => {
  return (
    <div className="space-y-6">
      {/* Facilities Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Facilities Overview</CardTitle>
          <CardDescription className="text-xs">
            {sites.length} active facilities
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Facility</th>
                <th className="text-right px-4 py-2 font-medium">Active Clients</th>
                <th className="text-right px-4 py-2 font-medium">Beds Available</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s: any) => {
                const siteRooms = rooms.filter((r: any) => r.site_id === s.id);
                const siteCap = siteRooms.reduce((sum: number, r: any) => sum + (r.capacity || 1), 0);
                const siteOccupied = clients.filter((c: any) => c.site_id === s.id && c.room_id && !c.archived_at).length;
                const siteActive = clients.filter((c: any) => c.site_id === s.id && !c.archived_at).length;
                return (
                  <tr
                    key={s.id}
                    className={`border-t border-border hover:bg-muted/30 ${onSelectFacility ? 'cursor-pointer' : ''}`}
                    onClick={() => onSelectFacility?.(s.id)}
                  >
                    <td className="px-4 py-2 font-medium">{s.name}</td>
                    <td className="px-4 py-2 text-right">{siteActive}</td>
                    <td className="px-4 py-2 text-right">{Math.max(0, siteCap - siteOccupied)}</td>
                  </tr>
                );
              })}
              {sites.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">No facilities yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Agency Analytics */}
      <AnalyticsHub
        clients={clients}
        alerts={alerts}
        totalCapacity={totalCapacity}
        checkedInCount={checkedInCount}
        dailyRosterCount={dailyRosterCount}
        userName={userName}
      />

      {showPerformanceCard && <PerformanceCard />}
    </div>
  );
};

export default SuperAdminHome;
